import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Mail, MailOpen } from "lucide-react";
import { cn } from "@/lib/utils";

// Emoji reactions based on Figma design
const REACTION_EMOJIS = ["😍", "😆", "😡", "😭", "😅", "👍", "👎"] as const;

export type ReactionEmoji = (typeof REACTION_EMOJIS)[number];

interface EntityActionBarProps {
  onReaction?: (emoji: ReactionEmoji) => void;
  onComment?: () => void;
  onMarkAsRead?: () => void;
  isRead?: boolean;
  className?: string;
}

export function EntityActionBar({
  onReaction,
  onComment,
  onMarkAsRead,
  isRead = false,
  className,
}: EntityActionBarProps) {
  return (
    <div className={cn("space-y-3", className)}>
      {/* Emoji Reactions Row */}
      <div className="flex items-center justify-center gap-2">
        {REACTION_EMOJIS.map((emoji) => (
          <button
            key={emoji}
            type="button"
            onClick={() => onReaction?.(emoji)}
            className="text-2xl transition-transform hover:scale-125 active:scale-95"
            aria-label={`React with ${emoji}`}
          >
            {emoji}
          </button>
        ))}
      </div>

      {/* Action Buttons Row */}
      <div className="flex items-center gap-3">
        <Button
          variant="outline"
          onClick={onComment}
          className="flex-1 rounded-full border-gray-300 hover:bg-gray-50"
        >
          Comment
        </Button>

        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              onClick={onMarkAsRead}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-300 text-gray-600 transition-colors hover:bg-gray-50"
              aria-label={isRead ? "Mark as unread" : "Mark as read"}
            >
              {isRead ? (
                <MailOpen className="h-5 w-5" />
              ) : (
                <Mail className="h-5 w-5" />
              )}
            </button>
          </TooltipTrigger>
          <TooltipContent>
            <p>{isRead ? "Mark as unread" : "Mark as read"}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
